import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { Toggle } from '@/components/shared'
import { ROUTES } from '@/constants/routeConstants'
import { useCookieConsent } from '@/contexts/CookieConsentContext'

interface CookiePreferences {
  analytics: boolean
  functional: boolean
}

export const CookieConsent = () => {
  const { t } = useTranslation()
  const { hasConsent, updateConsent } = useCookieConsent()
  const [visible, setVisible] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [prefs, setPrefs] = useState<CookiePreferences>({
    analytics: false,
    functional: false,
  })

  useEffect(() => {
    if (!hasConsent) {
      // Small delay so the page renders first
      const timer = setTimeout(() => setVisible(true), 600)

      return () => clearTimeout(timer)
    }
  }, [hasConsent])

  if (!visible) return null

  const save = (choices: CookiePreferences) => {
    updateConsent(choices.analytics, choices.functional)
    setVisible(false)
  }

  const handleAcceptAll = () => save({ analytics: true, functional: true })
  const handleRefuseAll = () => save({ analytics: false, functional: false })
  const handleSaveCustom = () => save(prefs)

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('cookieConsent.ariaLabel')}
      className="fixed bottom-0 left-0 right-0 z-[999] bg-white shadow-[0_-4px_32px_rgba(0,0,0,0.12)] transition-transform duration-300
  border-t-4 border-transparent bg-[linear-gradient(white,white),linear-gradient(90deg,var(--color-orange),var(--color-fuschia))] bg-origin-border [background-clip:padding-box,_border-box]
  max-h-[85vh] overflow-y-auto"
    >
      <div className="mx-auto max-w-[1200px] px-8 py-6">
        {!expanded && (
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex min-w-[260px] flex-1 items-start gap-2 md:gap-4">
              <span className="mt-0.5 flex-shrink-0 text-2xl">🍪</span>
              <div>
                <p className="mb-1 text-xl font-bold">
                  {t('cookieConsent.compact.title')}
                </p>
                <p className="max-w-[600px] text-sm leading-[1.6]">
                  {t('cookieConsent.compact.description')}{' '}
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="cursor-pointer border-none bg-transparent p-0 font-medium text-warning underline transition-opacity hover:opacity-80"
                  >
                    {t('cookieConsent.compact.customize')}
                  </button>
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 md:flex-shrink-0">
              <button
                type="button"
                onClick={handleRefuseAll}
                className="w-full cursor-pointer rounded-xl border-2 border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold transition-colors hover:shadow-1 md:w-auto"
              >
                {t('cookieConsent.actions.refuseAll')}
              </button>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="w-full cursor-pointer rounded-xl border-2 border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold transition-colors hover:shadow-1 md:w-auto"
              >
                {t('cookieConsent.actions.customize')}
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="w-full cursor-pointer rounded-xl bg-gradient-orange-fuschia px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:shadow-1 md:w-auto"
              >
                {t('cookieConsent.actions.acceptAll')}
              </button>
            </div>
          </div>
        )}

        {expanded && (
          <div>
            <div className="mb-5 flex items-center justify-between">
              <div className="">
                <div>
                  <p className="text-xl font-bold flex items-center gap-3">
                    <span className="text-2xl">🍪</span>
                    {t('cookieConsent.expanded.title')}
                  </p>
                  <p className="mt-0.5 text-xs">
                    {t('cookieConsent.expanded.subtitle')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="cursor-pointer border-none bg-transparent p-1 text-xl leading-none transition-colors self-start md:self-auto"
                aria-label={t('cookieConsent.expanded.collapse')}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border-2 border-orange/30 bg-black-50 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold">
                    {t('cookieConsent.categories.necessary.label')}
                  </span>
                  <span className="rounded-xl bg-gradient-orange-fuschia px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                    {t('cookieConsent.categories.necessary.badge')}
                  </span>
                </div>
                <p className="text-xs leading-[1.55]">
                  {t('cookieConsent.categories.necessary.description')}
                </p>
              </div>

              <div
                className={`cursor-pointer rounded-xl border-2 p-4 transition-colors
                              ${prefs.analytics ? 'border-warning/30 bg-[rgba(247,120,46,0.05)]' : 'border-gray-200 bg-black-50'}`}
                onClick={() =>
                  setPrefs((p) => ({ ...p, analytics: !p.analytics }))
                }
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold">
                    {t('cookieConsent.categories.analytics.label')}
                  </span>
                  <Toggle
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  />
                </div>
                <p className="text-xs leading-[1.55]">
                  {t('cookieConsent.categories.analytics.description')}
                </p>
              </div>

              <div
                className={`cursor-pointer rounded-xl border-2 p-4 transition-colors
                              ${prefs.functional ? 'border-warning/30 bg-[rgba(247,120,46,0.05)]' : 'border-gray-200 bg-black-50'}`}
                onClick={() =>
                  setPrefs((p) => ({ ...p, functional: !p.functional }))
                }
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold">
                    {t('cookieConsent.categories.functional.label')}
                  </span>
                  <Toggle
                    checked={prefs.functional}
                    onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
                  />
                </div>
                <p className="text-xs leading-[1.55]">
                  {t('cookieConsent.categories.functional.description')}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
              <Link
                to={ROUTES.COOKIE_PREFERENCES}
                className="text-xs text-warning underline transition-opacity hover:opacity-80"
              >
                {t('cookieConsent.expanded.learnMore')}
              </Link>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleRefuseAll}
                  className="w-full cursor-pointer rounded-xl border-2 border-gray-300 bg-white p-4 py-2.5 text-sm font-semibold transition-colors hover:shadow-1 md:w-auto"
                >
                  {t('cookieConsent.actions.refuseAll')}
                </button>
                <button
                  type="button"
                  onClick={handleSaveCustom}
                  className="w-full px-5 py-1.5 rounded-lg transition font-bold text-transparent 
                  border-2 border-transparent bg-[linear-gradient(90deg,var(--color-orange),var(--color-fuschia)),linear-gradient(white,white),linear-gradient(90deg,var(--color-orange),var(--color-fuschia))] bg-origin-border [background-clip:text,_padding-box,_border-box]
                  cursor-pointer hover:shadow-1 md:w-auto"
                >
                  {t('cookieConsent.actions.saveCustom')}
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="w-full cursor-pointer rounded-xl bg-gradient-orange-fuschia p-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:shadow-1 md:w-auto"
                >
                  {t('cookieConsent.actions.acceptAll')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
